import { Question, SurveyResponse } from "@/types/survey";

interface AIResponse {
  success: boolean;
  data?: {
    answers?: SurveyResponse;
    explanation: string;
    confidence: number;
    questionExplanations: {[questionId: number]: string};
  };
  error?: string;
}

export const gatherDataWithAI = async (
  projectName: string,
  industry: string,
  description: string,
  questions: Question[]
): Promise<AIResponse> => {
  // In a real implementation, this would call an AI service API
  // For now, we'll simulate the AI response with pre-defined logic
  
  try {
    // Simulate API request delay (1-2 seconds)
    await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 1000));
    
    // Generate methodological guidance for each question
    const questionExplanations: {[questionId: number]: string} = {};
    
    // Simulating AI-generated guidance with improved, more dynamic content
    questions.forEach(question => {
      questionExplanations[question.id] = generateGuidanceForQuestion(
        question, 
        description,
        projectName,
        industry
      );
    });
    
    // Generate overall explanation of methodology
    const explanation = generateMethodologyExplanation(
      projectName,
      industry,
      description,
      questions
    );
    
    // Mock confidence score (higher for longer descriptions)
    const confidence = Math.min(0.5 + (description.length / 500) * 0.5, 0.95);
    
    return {
      success: true,
      data: {
        explanation,
        confidence,
        questionExplanations
      }
    };
    
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred"
    };
  }
};

// Generate guidance for individual question with improved personalization
const generateGuidanceForQuestion = (
  question: Question,
  description: string,
  projectName: string,
  industry: string
): string => {
  const descLower = description.toLowerCase();
  const descWords = descLower.split(/\s+/);
  const projectNameLower = projectName.toLowerCase();
  
  // Extract key themes from the project description
  const keyThemes = new Set<string>();
  const themeWords = [
    'community', 'user', 'experience', 'design', 'interface', 'security',
    'privacy', 'data', 'ethical', 'sustainability', 'innovative', 'solution',
    'problem', 'customer', 'market', 'technology', 'platform', 'service',
    'product', 'engagement', 'trust', 'transparent', 'feedback', 'value'
  ];
  
  themeWords.forEach(theme => {
    if (descLower.includes(theme)) {
      keyThemes.add(theme);
    }
  });
  
  // Create personalized intro using project name and industry context
  const intros = [
    `For ${projectName} in the ${industry} space, consider how`,
    `When evaluating ${projectName} through the BEAM methodology lens,`,
    `In the context of your ${industry} project ${projectName},`,
    `The ${industry} industry has unique characteristics that ${projectName} should address in this area:`
  ];
  
  // Make selection semi-deterministic based on question ID to avoid repetition
  const introIndex = question.id % intros.length;
  const personalizedIntro = intros[introIndex];
  
  // Generate category-specific guidance with added personalization
  switch (question.category) {
    case 'ea': {
      const emotionalPrompts = [
        `How does ${projectName} create emotional resonance with users? Think about brand recognition, community loyalty, and meaningful interactions that generate positive feelings.`,
        `${projectName}'s emotional connection with users is crucial. Consider how user interface choices, brand messaging, and community features contribute to this relationship.`,
        `Emotional attachment in ${industry} often comes from ${keyThemes.size > 0 ? Array.from(keyThemes).slice(0, 2).join(' and ') : 'user experience and design'}. How does ${projectName} leverage these elements?`
      ];
      
      // Select based on question ID for variety
      const promptIndex = (question.id * 3) % emotionalPrompts.length;
      const mainPrompt = emotionalPrompts[promptIndex];
      
      // Add industry-specific insights
      let industryInsight = '';
      if (industry === 'Technology') {
        industryInsight = `In technology, emotional connections often form through intuitive interfaces, personalization, and solving genuine pain points.`;
      } else if (industry === 'Healthcare') {
        industryInsight = `Healthcare solutions that demonstrate empathy, trust, and genuine care create strong emotional bonds with users.`;
      } else if (industry === 'Finance') {
        industryInsight = `Financial products can build emotional connection through security, transparency, and empowering users with knowledge and control.`;
      } else if (industry === 'Web 3.0') {
        industryInsight = `In Web 3.0, community ownership, decentralized governance, and user empowerment create strong emotional ties to projects.`;
      } else {
        industryInsight = `In ${industry}, emotional connections form when products genuinely solve problems and create meaningful user experiences.`;
      }
      
      // Create project-specific examples based on description
      let examples = '';
      if (descLower.includes('community')) {
        examples += `\n\nYour mention of community suggests opportunities for fostering belonging and shared experiences. How do you measure community engagement?`;
      }
      if (descLower.includes('user') && (descLower.includes('experience') || descLower.includes('interface'))) {
        examples += `\n\nYou've highlighted user experience. Consider specific interaction points where emotional connection is strengthened or weakened.`;
      }
      
      // Create comprehensive guidance
      return `${personalizedIntro} ${mainPrompt}\n\n${industryInsight}${examples}\n\nConsider addressing:\n• Brand recognition factors\n• Community engagement metrics\n• Specific emotional responses you aim to evoke\n• How these translate to measurable outcomes`;
    }
    
    case 'elv': {
      const legacyPrompts = [
        `How does ${projectName} create lasting value beyond immediate utility? Consider ethical foundations, transformative potential, and positive industry impact.`,
        `${projectName}'s legacy value stems from its ethical stance and long-term vision. How do you balance short-term goals with creating enduring change?`,
        `In ${industry}, legacy is built through ${keyThemes.size > 0 ? Array.from(keyThemes).slice(0, 2).join(' and ') : 'innovation and ethical practices'}. How does ${projectName} approach this balance?`
      ];
      
      // Select based on question ID for variety
      const promptIndex = (question.id * 3) % legacyPrompts.length;
      const mainPrompt = legacyPrompts[promptIndex];
      
      // Add industry-specific insights
      let industryInsight = '';
      if (industry === 'Technology') {
        industryInsight = `Technology that respects user privacy, promotes wellbeing, and solves meaningful problems creates enduring ethical value.`;
      } else if (industry === 'Healthcare') {
        industryInsight = `Healthcare solutions that improve access, enhance outcomes, and address systemic inequities build strong legacy value.`;
      } else if (industry === 'Finance') {
        industryInsight = `Financial products that promote inclusion, education, and user empowerment create meaningful ethical impact.`;
      } else if (industry === 'Web 3.0') {
        industryInsight = `Web 3.0 projects that balance innovation with responsibility, privacy, and user control establish long-term ethical foundations.`;
      } else {
        industryInsight = `In ${industry}, legacy value comes from sustainable practices, ethical choices, and positive stakeholder impact.`;
      }
      
      // Create project-specific examples based on description
      let examples = '';
      if (descLower.includes('ethic') || descLower.includes('responsib') || descLower.includes('sustain')) {
        examples += `\n\nYour focus on ethical considerations suggests strong alignment with legacy value creation. How do you measure this impact?`;
      }
      if (descLower.includes('future') || descLower.includes('vision') || descLower.includes('mission')) {
        examples += `\n\nYour long-term vision aligns with legacy value. Consider articulating specific goals and impact metrics in your assessment.`;
      }
      
      return `${personalizedIntro} ${mainPrompt}\n\n${industryInsight}${examples}\n\nIn your response, consider including:\n• Ethical frameworks guiding decisions\n• Long-term industry contributions\n• Values alignment with stakeholders\n• Measurement approaches for legacy impact`;
    }
    
    case 'l':
    case 'fc': {
      const frictionPrompts = [
        `How does ${projectName} identify and address potential friction points in the user journey? Consider barriers to adoption, usage challenges, and mitigation strategies.`,
        `${projectName}'s approach to reducing friction is crucial for user retention. How do you balance feature richness with simplicity and accessibility?`,
        `In ${industry}, common friction points include ${keyThemes.size > 0 ? Array.from(keyThemes).slice(0, 2).join(' and ') : 'complexity and user onboarding'}. How does ${projectName} address these challenges?`
      ];
      
      // Select based on question ID for variety
      const promptIndex = (question.id * 3) % frictionPrompts.length;
      const mainPrompt = frictionPrompts[promptIndex];
      
      // Add industry-specific insights
      let industryInsight = '';
      if (industry === 'Technology') {
        industryInsight = `Tech products must balance feature richness with intuitive design to minimize cognitive friction and maximize user satisfaction.`;
      } else if (industry === 'Healthcare') {
        industryInsight = `Healthcare solutions often face adoption barriers related to trust, privacy concerns, and integration with existing systems.`;
      } else if (industry === 'Finance') {
        industryInsight = `Financial products need to address complexity, security concerns, and educational barriers that create friction for users.`;
      } else if (industry === 'Web 3.0') {
        industryInsight = `Web 3.0 projects must overcome significant friction related to technical complexity, onboarding hurdles, and user education.`;
      } else {
        industryInsight = `In ${industry}, understanding and addressing user pain points is essential for reducing friction and boosting engagement.`;
      }
      
      // Create project-specific examples based on description
      let examples = '';
      if (descLower.includes('simple') || descLower.includes('ease') || descLower.includes('intuitive')) {
        examples += `\n\nYour emphasis on simplicity suggests awareness of friction challenges. How do you measure usability improvements?`;
      }
      if (descLower.includes('onboarding') || descLower.includes('learn') || descLower.includes('guide')) {
        examples += `\n\nYour attention to user onboarding is important for friction reduction. Consider specific metrics for measuring the effectiveness of these processes.`;
      }
      
      return `${personalizedIntro} ${mainPrompt}\n\n${industryInsight}${examples}\n\nConsider addressing in your response:\n• Specific friction points identified in user journey\n• Quantitative metrics for measuring friction\n• Comparison to industry benchmarks\n• Ongoing monitoring and improvement processes`;
    }
    
    case 'tb': {
      const trustPrompts = [
        `How does ${projectName} establish and maintain trust with users and stakeholders? Consider transparency practices, consistency in delivery, and credibility signals.`,
        `${projectName}'s trust foundation relies on multiple factors. How do you ensure promises align with actual user experiences?`,
        `In ${industry}, trust is built through ${keyThemes.size > 0 ? Array.from(keyThemes).slice(0, 2).join(' and ') : 'transparency and consistency'}. How does ${projectName} leverage these elements?`
      ];
      
      // Select based on question ID for variety
      const promptIndex = (question.id * 3) % trustPrompts.length;
      const mainPrompt = trustPrompts[promptIndex];
      
      // Add industry-specific insights
      let industryInsight = '';
      if (industry === 'Technology') {
        industryInsight = `Tech products build trust through transparent data practices, reliable performance, and clear communication about capabilities and limitations.`;
      } else if (industry === 'Healthcare') {
        industryInsight = `Healthcare solutions require exceptional trust foundations related to privacy, accuracy, and outcomes-focused approaches.`;
      } else if (industry === 'Finance') {
        industryInsight = `Financial products must address heightened trust concerns through security, transparency, and consistent value delivery.`;
      } else if (industry === 'Web 3.0') {
        industryInsight = `Web 3.0 projects face unique trust challenges related to security, verification, and balancing innovation with stability.`;
      } else {
        industryInsight = `In ${industry}, trust is earned through consistent value delivery, transparent practices, and responsive communication.`;
      }
      
      // Create project-specific examples based on description
      let examples = '';
      if (descLower.includes('transpar') || descLower.includes('honest') || descLower.includes('open')) {
        examples += `\n\nYour commitment to transparency supports trust-building. How do you ensure this translates to user confidence?`;
      }
      if (descLower.includes('secur') || descLower.includes('protect') || descLower.includes('privacy')) {
        examples += `\n\nYour focus on security/privacy is crucial for trust. Consider metrics for measuring user confidence in these areas.`;
      }
      
      return `${personalizedIntro} ${mainPrompt}\n\n${industryInsight}${examples}\n\nIn your response, consider including:\n• Specific trust-building mechanisms\n• How you measure trust with stakeholders\n• Third-party validation approaches\n• How feedback influences trust-building practices`;
    }
    
    default:
      // Generic guidance for other categories
      return `Consider how ${projectName} addresses this question within the ${industry} industry context. Focus on specific examples, metrics, and methodologies. The BEAM evaluation framework looks for both quantitative measurement and qualitative insights in this area.`;
  }
};

// Generate overall methodology explanation with improved personalization
const generateMethodologyExplanation = (
  projectName: string,
  industry: string,
  description: string,
  questions: Question[]
): string => {
  // Create an explanation of the BEAM methodology tailored to the project
  const categories = [
    { key: 'ea', name: 'Emotional Attachment', found: false },
    { key: 'elv', name: 'Ethical & Legacy Value', found: false },
    { key: 'l', name: 'Longevity', found: false },
    { key: 'fc', name: 'Friction & Conversion', found: false },
    { key: 'tb', name: 'Trust Building', found: false },
  ];
  
  // Mark which categories are present in the questions
  questions.forEach(q => {
    const category = categories.find(c => c.key === q.category);
    if (category) {
      category.found = true;
    }
  });
  
  // Extract key themes from the project description
  const descLower = description.toLowerCase();
  const keyThemes = [];
  const themeWords = [
    'community', 'user', 'experience', 'design', 'interface', 'security',
    'privacy', 'data', 'ethical', 'sustainability', 'innovative', 'solution',
    'problem', 'customer', 'market', 'technology', 'platform', 'service',
    'product', 'engagement', 'trust', 'transparent', 'feedback', 'value'
  ];
  
  themeWords.forEach(theme => {
    if (descLower.includes(theme)) {
      keyThemes.push(theme);
    }
  });
  
  // Create specific industry context with personalization
  let industryContext = '';
  switch (industry) {
    case 'Technology':
      industryContext = `In the technology sector, BEAM methodology emphasizes innovation cycles, user experience design, and ethical technology practices. For ${projectName}, this means evaluating how your ${keyThemes.length > 0 ? keyThemes.slice(0, 3).join(', ') : 'features and user experience'} create meaningful connections beyond functionality.`;
      break;
    case 'Healthcare':
      industryContext = `For healthcare organizations like ${projectName}, BEAM methodology focuses on patient outcomes, care accessibility, and trust-building practices. The assessment will help quantify how your ${keyThemes.length > 0 ? keyThemes.slice(0, 3).join(', ') : 'solutions and services'} create both functional and emotional value.`;
      break;
    case 'Finance':
      industryContext = `In financial services, BEAM methodology evaluates trust mechanisms, transparency practices, and client relationships. For ${projectName}, this means assessing how your ${keyThemes.length > 0 ? keyThemes.slice(0, 3).join(', ') : 'offerings and processes'} create security and confidence beyond basic transactions.`;
      break;
    case 'Web 3.0':
      industryContext = `For Web 3.0 projects like ${projectName}, BEAM methodology examines community engagement, decentralization principles, and trust mechanisms. The assessment will evaluate how your ${keyThemes.length > 0 ? keyThemes.slice(0, 3).join(', ') : 'platform and innovations'} build emotional equity and long-term value.`;
      break;
    case 'Education':
      industryContext = `For educational initiatives like ${projectName}, BEAM methodology emphasizes learning outcomes, accessibility, and community building. This assessment helps quantify how your ${keyThemes.length > 0 ? keyThemes.slice(0, 3).join(', ') : 'programs and materials'} create emotional dimensions in the learning experience.`;
      break;
    default:
      industryContext = `In the ${industry} industry, BEAM methodology provides a structured framework for evaluating how ${projectName}'s ${keyThemes.length > 0 ? keyThemes.slice(0, 3).join(', ') : 'offerings and approach'} create emotional and ethical dimensions alongside traditional metrics.`;
  }
  
  // Personalized guidance based on description length and content
  let personalizationTip = '';
  if (description.length < 50) {
    personalizationTip = "For more tailored guidance, consider providing additional details about your specific features, organizational values, and long-term vision.";
  } else if (keyThemes.length < 3) {
    personalizationTip = "To enhance this guidance, consider elaborating on your project's unique approaches to user experience, ethical considerations, and trust-building practices.";
  }
  
  // Assemble the complete explanation
  return `The AI methodology guide will help you develop thoughtful responses for ${projectName} using BEAM's proprietary diagnostic framework. We'll focus on ${categories.filter(c => c.found).map(c => c.name).join(', ')}.\n\n${industryContext}\n\nWhen responding to each question, consider both qualitative and quantitative aspects, providing specific examples and metrics where possible. The guidance aims to help you articulate your project's emotional equity in a structured way that aligns with BEAM evaluation standards.\n\n${personalizationTip}`;
};

// Helper function to generate a score based on description keywords
const generateScoreBasedOnDescription = (
  question: Question,
  description: string,
  industry: string
): number | 'Y' | 'N' => {
  const descLower = description.toLowerCase();
  const words = descLower.split(/\s+/);
  
  if (question.type === 'binary') {
    // For binary questions, look for positive indicators
    const positiveKeywords = [
      'sustainable', 'ethical', 'transparent', 'community', 
      'feedback', 'support', 'responsive', 'help', 'assist',
      'resolve', 'solution', 'address'
    ];
    
    const hasPositiveIndicator = positiveKeywords.some(keyword => 
      descLower.includes(keyword)
    );
    
    // Special case for redemption sink question
    if (question.id === 11) {
      if (descLower.includes('feedback') || 
          descLower.includes('complaint') || 
          descLower.includes('resolve') ||
          descLower.includes('customer service') ||
          descLower.includes('support')) {
        return 'Y';
      }
    }
    
    return hasPositiveIndicator ? 'Y' : 'N';
  }
  
  // For Likert questions, calculate score based on various factors
  let baseScore = 3; // Default neutral score
  
  // Category-specific keywords that increase the score
  const categoryKeywords: Record<string, string[]> = {
    'ea': ['community', 'love', 'passion', 'fan', 'engage', 'excite', 'delightful', 'brand', 'loyal'],
    'elv': ['ethical', 'transparent', 'sustainable', 'responsible', 'impact', 'legacy', 'future'],
    'l': ['sustainable', 'durable', 'long-term', 'adaptable', 'resilient', 'stable', 'growth'],
    'ppr': ['improve', 'solution', 'problem', 'pain', 'resolve', 'address', 'fix', 'help']
  };
  
  // Count keywords for this question's category
  const relevantKeywords = categoryKeywords[question.category] || [];
  let keywordMatches = 0;
  
  relevantKeywords.forEach(keyword => {
    if (descLower.includes(keyword.toLowerCase())) {
      keywordMatches++;
    }
  });
  
  // Adjust score based on keyword matches
  baseScore = Math.min(5, baseScore + (keywordMatches * 0.5));
  
  // Industry-specific adjustments
  if (industry === 'Technology' && (question.id === 12 || question.id === 13)) {
    // Innovation and relevance questions score higher for tech
    baseScore = Math.min(5, baseScore + 0.5);
  } else if (industry === 'Healthcare' && (question.id === 6 || question.id === 7)) {
    // Ethics questions score higher for healthcare
    baseScore = Math.min(5, baseScore + 0.5);
  }
  
  // Length adjustment - more detailed descriptions suggest more thought/effort
  const lengthBonus = Math.min(0.5, description.length / 500);
  baseScore = Math.min(5, baseScore + lengthBonus);
  
  // Add some variance
  const variance = (Math.random() * 0.6) - 0.3; // -0.3 to +0.3
  baseScore = Math.max(1, Math.min(5, baseScore + variance));
  
  // Round to nearest 0.5 for more natural scoring
  return Math.round(baseScore * 2) / 2;
};

// Generate explanation for individual question
const generateExplanationForQuestion = (
  question: Question,
  score: number | 'Y' | 'N',
  description: string,
  projectName: string,
  industry: string
): string => {
  const descLower = description.toLowerCase();
  const scoreText = typeof score === 'number' ? `${score}/5` : (score === 'Y' ? 'Yes' : 'No');
  
  // Common phrases to make explanations sound more natural
  const analyses = [
    `Based on the project description, ${projectName} appears to`,
    `The AI analysis indicates that ${projectName}`,
    `From the provided information, it seems that ${projectName}`,
    `Taking into account the ${industry} industry context, ${projectName}`,
    `According to the project details,`
  ];
  
  const randomAnalysis = analyses[Math.floor(Math.random() * analyses.length)];
  
  // Generate specific explanations for each question category
  switch (question.category) {
    case 'ea': {
      const emotionalPhrases = [
        `demonstrates ${typeof score === 'number' && score >= 4 ? 'strong' : typeof score === 'number' && score >= 3 ? 'moderate' : 'limited'} emotional connection potential with users`,
        `has created a brand that's likely to establish ${typeof score === 'number' && score >= 4 ? 'significant' : typeof score === 'number' && score >= 3 ? 'decent' : 'minimal'} emotional resonance`,
        `focuses on ${typeof score === 'number' && score >= 4 ? 'enhancing' : typeof score === 'number' && score >= 3 ? 'acknowledging' : 'somewhat neglecting'} user emotional attachment`
      ];
      
      const randomPhrase = emotionalPhrases[Math.floor(Math.random() * emotionalPhrases.length)];
      
      if (question.id === 1) {
        return `${randomAnalysis} ${randomPhrase} through its ${descLower.includes('design') ? 'design approach' : 'product features'}. The BEAM NEV model gives this a rating of ${scoreText} as it ${typeof score === 'number' && score >= 4 ? 'clearly prioritizes' : typeof score === 'number' && score >= 3 ? 'considers' : 'could better address'} user delight.`;
      } else if (question.id === 2 || question.id === 3) {
        return `${randomAnalysis} ${randomPhrase}. Based on ${industry} industry benchmarks, the community engagement potential rates ${scoreText}, suggesting ${typeof score === 'number' && score >= 4 ? 'above average' : typeof score === 'number' && score >= 3 ? 'average' : 'below average'} advocacy potential compared to similar projects.`;
      } else {
        return `${randomAnalysis} ${randomPhrase}, receiving a ${scoreText} rating in this emotional attachment metric. ${typeof score === 'number' && score >= 4 ? 'This strength can become a competitive advantage.' : typeof score === 'number' && score >= 3 ? 'This is on par with industry standards.' : 'This represents a potential area for improvement.'}`;
      }
    }
    
    case 'elv': {
      const ethicalPhrases = [
        `demonstrates ${typeof score === 'number' && score >= 4 ? 'strong' : typeof score === 'number' && score >= 3 ? 'adequate' : 'limited'} ethical considerations`,
        `shows ${typeof score === 'number' && score >= 4 ? 'above average' : typeof score === 'number' && score >= 3 ? 'standard' : 'below average'} commitment to ethical practices`,
        `${typeof score === 'number' && score >= 4 ? 'prioritizes' : typeof score === 'number' && score >= 3 ? 'acknowledges' : 'could improve on'} ethical value creation`
      ];
      
      const randomPhrase = ethicalPhrases[Math.floor(Math.random() * ethicalPhrases.length)];
      
      if (question.id === 11) {
        // Redemption sink question
        if (score === 'Y') {
          return `${randomAnalysis} has implemented systems to address user concerns and complaints, which earns a "Yes" rating. This is a significant positive factor in the BEAM NEV model, as redemption systems are crucial for long-term trust.`;
        } else {
          return `${randomAnalysis} doesn't appear to have clearly defined systems for addressing user concerns or complaints based on the provided description, resulting in a "No" rating. Adding visible complaint resolution mechanisms could significantly improve the ELV score (+2 points).`;
        }
      } else {
        return `${randomAnalysis} ${randomPhrase}, scoring ${scoreText} in this ethical dimension. ${typeof score === 'number' && score >= 4 ? 'This represents strong alignment with stakeholder values.' : typeof score === 'number' && score >= 3 ? 'This indicates standard industry practices are being followed.' : 'This suggests an opportunity to strengthen ethical practices.'} In the ${industry} industry, ${typeof score === 'number' && score >= 4 ? 'this is a differentiating factor.' : 'ethical considerations are increasingly important.'}`;
      }
    }
    
    case 'l': {
      const longevityPhrases = [
        `demonstrates ${typeof score === 'number' && score >= 4 ? 'high' : typeof score === 'number' && score >= 3 ? 'moderate' : 'concerning'} longevity indicators`,
        `shows ${typeof score === 'number' && score >= 4 ? 'strong' : typeof score === 'number' && score >= 3 ? 'adequate' : 'limited'} potential for sustained relevance`,
        `has ${typeof score === 'number' && score >= 4 ? 'good' : typeof score === 'number' && score >= 3 ? 'fair' : 'questionable'} long-term viability`
      ];
      
      const randomPhrase = longevityPhrases[Math.floor(Math.random() * longevityPhrases.length)];
      
      return `${randomAnalysis} ${randomPhrase}, receiving a ${scoreText} rating for this metric. ${typeof score === 'number' && score >= 4 ? 'This suggests the project has strong staying power.' : typeof score === 'number' && score >= 3 ? 'This indicates standard resilience factors are present.' : 'This highlights a potential vulnerability in the long-term strategy.'} In the ${industry} sector, ${typeof score === 'number' && score >= 4 ? 'this level of sustainability planning is exemplary.' : typeof score === 'number' && score >= 3 ? 'this level of planning is typical.' : 'more robust planning is typically expected.'}`;
    }
    
    case 'ppr': {
      const pprPhrases = [
        `shows ${typeof score === 'number' && score >= 4 ? 'excellent' : typeof score === 'number' && score >= 3 ? 'adequate' : 'concerning'} pain-to-purpose ratio`,
        `demonstrates ${typeof score === 'number' && score >= 4 ? 'strong' : typeof score === 'number' && score >= 3 ? 'reasonable' : 'weak'} ability to resolve user frustrations`,
        `has ${typeof score === 'number' && score >= 4 ? 'minimal' : typeof score === 'number' && score >= 3 ? 'manageable' : 'significant'} friction points in the user experience`
      ];
      
      const randomPhrase = pprPhrases[Math.floor(Math.random() * pprPhrases.length)];
      
      return `${randomAnalysis} ${randomPhrase}, scoring ${scoreText} on this metric. ${typeof score === 'number' && score >= 4 ? 'This indicates frustration points are well-managed.' : typeof score === 'number' && score >= 3 ? 'This suggests typical user friction levels.' : 'This highlights potential user experience concerns.'} The BEAM methodology identifies this as ${typeof score === 'number' && score >= 4 ? 'a strength' : typeof score === 'number' && score >= 3 ? 'an average factor' : 'an area for targeted improvement'}.`;
    }
    
    default:
      return `Based on the provided information, this question received a score of ${scoreText}. The AI suggests reviewing the project's alignment with this metric's objectives.`;
  }
};

// Generate overall explanation
const generateOverallExplanation = (
  projectName: string,
  industry: string,
  description: string,
  answers: SurveyResponse,
  questions: Question[]
): string => {
  // Calculate average scores per category
  let eaTotal = 0;
  let eaCount = 0;
  let elvTotal = 0;
  let elvCount = 0;
  let lTotal = 0;
  let lCount = 0;
  
  questions.forEach(q => {
    const answer = answers[q.id];
    if (answer !== undefined) {
      const numericValue = typeof answer === 'number' ? answer : (answer === 'Y' ? 5 : 1);
      
      if (q.category === 'ea') {
        eaTotal += numericValue;
        eaCount++;
      } else if (q.category === 'elv') {
        elvTotal += numericValue;
        elvCount++;
      } else if (q.category === 'l') {
        lTotal += numericValue;
        lCount++;
      }
    }
  });
  
  const eaAvg = eaCount > 0 ? eaTotal / eaCount : 0;
  const elvAvg = elvCount > 0 ? elvTotal / elvCount : 0;
  const lAvg = lCount > 0 ? lTotal / lCount : 0;
  
  const hasSink = answers[11] === 'Y';
  const sinkText = hasSink ? 
    "A functioning redemption sink was identified, which is a significant positive factor (+2 points to ELV score)." : 
    "No clear redemption sink was identified in the project description.";
  
  // Generate category insights
  const eaInsight = eaAvg >= 4 ? 
    "Strong emotional attachment potential identified." : 
    eaAvg >= 3 ? 
    "Moderate emotional connection with users expected." : 
    "Limited emotional attachment indicators found.";
    
  const elvInsight = elvAvg >= 4 ? 
    "Excellent ethical and legacy value foundations." : 
    elvAvg >= 3 ? 
    "Adequate ethical considerations present." : 
    "Ethical framework could be strengthened.";
    
  const lInsight = lAvg >= 4 ? 
    "Strong longevity indicators predict sustained relevance." : 
    lAvg >= 3 ? 
    "Average resilience to market changes expected." : 
    "Longevity concerns may require strategic attention.";
  
  // Industry-specific insights
  let industryInsight;
  
  switch (industry) {
    case 'Technology':
      industryInsight = `In the technology sector, projects typically need strong innovation scores (${answers[12] ? `yours: ${answers[12]}/5` : "not enough data"}) and ethical data practices to maintain competitive advantage.`;
      break;
    case 'Healthcare':
      industryInsight = `Healthcare projects are generally held to higher ethical standards, with your ethics alignment score ${answers[7] ? `at ${answers[7]}/5` : "requiring assessment"} being a critical success factor.`;
      break;
    case 'Finance':
      industryInsight = `For financial projects, trust indicators (${answers[1] ? `currently ${answers[1]}/5` : "not yet assessed"}) and transparency are particularly important for user adoption.`;
      break;
    case 'Education':
      industryInsight = `Educational projects typically benefit from strong community engagement (${answers[3] ? `yours: ${answers[3]}/5` : "not enough data"}) and ethical foundations.`;
      break;
    default:
      industryInsight = `In the ${industry} industry, balancing emotional connection with ethical practices and longevity planning is essential for sustained success.`;
  }
  
  // Assemble the complete explanation
  return `The AI has evaluated "${projectName}" based on the BEAM methodology and your description. ${eaInsight} ${elvInsight} ${lInsight} ${sinkText} ${industryInsight} For more precise assessment, consider providing additional details about specific features, organizational values, and long-term vision.`;
};
